/* Copyright 2019 Ellucian Company L.P. and its affiliates.
 * File: PermissionRequestDetail.tsx
 * Type: Presentation component */

// #region Imports
import classnames from 'classnames';
import React from 'react';

// Core components
import Avatar from '@hedtech/powercampus-design-system/react/core/Avatar';
import Chip from '@hedtech/powercampus-design-system/react/core/Chip';
import Divider from '@hedtech/powercampus-design-system/react/core/Divider';
import Grid from '@hedtech/powercampus-design-system/react/core/Grid';
import Icon from '@hedtech/powercampus-design-system/react/core/Icon';
import Link from '@hedtech/powercampus-design-system/react/core/Link';
import StatusLabel, { StatusLabelType } from '@hedtech/powercampus-design-system/react/core/StatusLabel';
import Text from '@hedtech/powercampus-design-system/react/core/Text';
import TextField from '@hedtech/powercampus-design-system/react/core/TextField';
import Tooltip from '@hedtech/powercampus-design-system/react/core/Tooltip';

// Helpers
import Tokens from '@hedtech/powercampus-design-system/react/core/styles/Tokens';
import { createStyles, Theme, withStyles, WithStyles } from '@hedtech/powercampus-design-system/react/core/styles/withStyles';

// Types
import { IPermissionRequestStatusResources } from '../../../Types/Resources/Shared/IPermissionRequestStatusResources';
import { IPermissionRequestInfo } from '../../../Types/Students/IPermissionRequestInfo';
// #endregion Imports

// #region Types
export interface IPermissionRequestDetailProps {
    chatTitleAdorned?: boolean;
    generalStatus: number; // 0 = Approved, 1 = Denied, >=2 Pending
    id: string;
    isFaculty?: boolean;
    isStudent?: boolean;
    permissionRequestInfo: IPermissionRequestInfo[];
    resources: IPermissionRequestDetailResProps;
    withChatTitle?: boolean;
    withScroll?: boolean;
}

export interface IPermissionRequestDetailPropsToExtend {
    resourcesPermissionRequestStatus: IPermissionRequestStatusResources;
    onCancelEdit: (id: number) => void;
    onChangeCommentsForEdit: (id: number, value: string) => void;
    onEdit: (id: number) => void;
    onEnableEdit: (id: number) => void;
}

export interface IPermissionRequestDetailResProps {
    btnAccept: string;
    btnCancel: string;
    btnEdit: string;
    lblCommentRequired: string;
    lblComments: string;
    lblNoComments: string;
    lblStatusTitle: string;
    lblTypeComments: string;
}

const styles = (theme: Theme) => createStyles({
    anchorIcon: {
        '&:focus': {
            '& *': {
                backgroundColor: 'transparent'
            }
        }
    },
    centered: {
        alignItems: 'center',
        display: 'flex'
    },
    chatContainer: {
        display: 'block!important',
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: Tokens.spacing40,
        width: 'auto'
    },
    chatContainerScroll: {
        maxHeight: '200px'
    },
    chatTitle: {
        backgroundColor: Tokens.colorBrandNeutral200,
        boxShadow: '0 0 1.25rem rgba(0, 0, 0, .1)',
        height: '57px',
        padding: Tokens.spacing40
    },
    chipCommonComments: {
        backgroundColor: Tokens.colorBrandNeutral100,
        borderWidth: 0,
        boxShadow: '0 0 1.25rem rgba(0, 0, 0, .1)',
        marginBottom: Tokens.spacing40
    },
    chipEditableComments: {
        '& > span': {
            [theme.breakpoints.down('md')]: {
                display: 'block',
                textAlign: 'right'
            }
        },
        backgroundColor: Tokens.colorBrandNeutral100,
        borderWidth: 0,
        boxShadow: '0 0 1.25rem rgba(0, 0, 0, .1)',
        marginBottom: Tokens.spacing40
    },
    chipImportantComments: {
        backgroundColor: Tokens.iris600,
        borderWidth: 0,
        color: Tokens.colorBrandNeutral100,
        marginBottom: Tokens.spacing40
    },
    chipStatusComments: {
        backgroundColor: Tokens.colorBrandNeutral100,
        borderWidth: 0,
        boxShadow: '0 0 1.25rem rgba(0, 0, 0, .1)',
        marginLeft: Tokens.sizingXLarge
    },
    commentsForStudent: {
        width: '100%'
    },
    commentsInChip: {
        paddingBottom: Tokens.spacing30,
        paddingTop: Tokens.spacing30,
        whiteSpace: 'initial',
        wordBreak: 'normal'
    },
    commentsInChipEditable: {
        '& > div > textarea': {
            width: '100%'
        },
        paddingBottom: Tokens.spacing30,
        paddingTop: Tokens.spacing30
    },
    firstIcon: {
        marginLeft: Tokens.spacing30
    },
    overrideCommentsContainer: {
        marginLeft: Tokens.sizingXLarge
    },
    secondIcon: {
        marginLeft: Tokens.spacing50
    },
    textOverChip: {
        marginLeft: Tokens.spacing34,
        wordBreak: 'normal'
    }
});

type PropsWithStyles = IPermissionRequestDetailProps & IPermissionRequestDetailPropsToExtend & WithStyles<typeof styles>;
// #endregion Types

// #region Component
const PermissionRequestDetail: React.FC<PropsWithStyles> = (props: PropsWithStyles): JSX.Element => {
    const {
        chatTitleAdorned,
        classes,
        generalStatus,
        id,
        isFaculty,
        isStudent,
        permissionRequestInfo,
        resources,
        resourcesPermissionRequestStatus,
        withChatTitle,
        withScroll,
        onCancelEdit,
        onChangeCommentsForEdit,
        onEdit,
        onEnableEdit
    } = props;

    const getStatusText = (status: number): string => {
        let text = '';
        switch (status) {
            case 0:
                text = resourcesPermissionRequestStatus.lblApproved;
                break;
            case 1:
                text = resourcesPermissionRequestStatus.lblDenied;
                break;
            case 2:
                text = resourcesPermissionRequestStatus.lblPending;
                break;
        }
        return text;
    };

    const getStatusType = (status: number): StatusLabelType => {
        let type: StatusLabelType = 'default';
        switch (status) {
            case 0:
                type = 'success';
                break;
            case 1:
                type = 'error';
                break;
            case 2:
                type = 'draft';
                break;
        }
        return type;
    };

    return (
        <>
            {withChatTitle ? (
                <div className={chatTitleAdorned ? classes.chatTitle : undefined}>
                    <Grid container>
                        <Grid item>
                            <Text size="h4">
                                {resources.lblStatusTitle}
                            </Text>
                        </Grid>
                        <Grid item xs>
                            <StatusLabel
                                id={`lblStatusGeneral_${id}`}
                                text={getStatusText(generalStatus)}
                                type={getStatusType(generalStatus)}
                            />
                        </Grid>
                    </Grid>
                </div>
            ) : undefined}
            <div
                id={id}
                className={withScroll ?
                    classnames(classes.chatContainer, classes.chatContainerScroll)
                    : classes.chatContainer}
            >
                {permissionRequestInfo.map((permissionRequest, ipr) => {
                    const onChangeCommentsForEditWithId = (event: React.ChangeEvent<HTMLInputElement>): void => {
                        onChangeCommentsForEdit(permissionRequest.id, event.target.value);
                    };
                    const onClickCancelEdit = function (): void { onCancelEdit(permissionRequest.id); };
                    const onClickEdit = function (): void { onEdit(permissionRequest.id); };
                    const onClickEnableEdit = function (): void { onEnableEdit(permissionRequest.id); };
                    return (
                        <Grid container spacing={0} key={`permissionRequestInfo_${ipr}`}>
                            <Grid item xs={12}>
                                {permissionRequest.studentComments || permissionRequest.studentCommentsBackup ? (
                                    <Grid container justifyContent={isFaculty ? 'flex-start' : 'flex-end'}>
                                        <Grid item xs={isFaculty ? true : false}>
                                            <Text
                                                className={classes.textOverChip}
                                                color="textSecondary"
                                                size="small"
                                                verticalAlign="middle"
                                            >
                                                {permissionRequest.requestDate}
                                            </Text>
                                            {isStudent && permissionRequest.editable ? (
                                                <Grid container>
                                                    <Grid item xs>
                                                        <Chip
                                                            classes={{ root: classes.chipEditableComments }}
                                                            id="chpStudentEditableComments"
                                                            label={(
                                                                <>
                                                                    <TextField
                                                                        classes={{ root: classes.commentsInChipEditable }}
                                                                        error={permissionRequest.error}
                                                                        helperText={permissionRequest.error ?
                                                                            resources.lblCommentRequired : ''}
                                                                        id={`txtComments_${permissionRequest.id}`}
                                                                        label={resources.lblComments}
                                                                        multiline
                                                                        minRows={2}
                                                                        type="text"
                                                                        value={permissionRequest.studentComments || ''}
                                                                        onChange={onChangeCommentsForEditWithId}
                                                                    />
                                                                    <Link
                                                                        classes={{ anchor: classes.anchorIcon }}
                                                                        className={classes.firstIcon}
                                                                        id={`btnEdit_${permissionRequest.id}`}
                                                                        onClick={onClickEdit}
                                                                    >
                                                                        <Tooltip
                                                                            id={`tltEdit_${permissionRequest.id}`}
                                                                            title={resources.btnAccept}
                                                                            aria-label={resources.btnAccept}
                                                                        >
                                                                            <Icon
                                                                                large
                                                                                name="check-feedback"
                                                                                type="success"
                                                                            />
                                                                        </Tooltip>
                                                                    </Link>
                                                                    <Link
                                                                        classes={{ anchor: classes.anchorIcon }}
                                                                        className={classes.secondIcon}
                                                                        id={`btnCancelEdit_${permissionRequest.id}`}
                                                                        onClick={onClickCancelEdit}
                                                                    >
                                                                        <Tooltip
                                                                            id={`tltCancelEdit_${permissionRequest.id}`}
                                                                            title={resources.btnCancel}
                                                                            aria-label={resources.btnCancel}
                                                                        >
                                                                            <Icon
                                                                                large
                                                                                name="close"
                                                                                type="error"
                                                                            />
                                                                        </Tooltip>
                                                                    </Link>
                                                                </>
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            ) : (
                                                    <Chip
                                                        classes={{ root: classes.chipImportantComments }}
                                                        id="chpStudentComments"
                                                        label={(
                                                            <>
                                                                <Text
                                                                    className={classes.commentsInChip}
                                                                    color="inherit"
                                                                    size="small"
                                                                >
                                                                    {permissionRequest.studentComments}
                                                                </Text>
                                                                {isStudent && generalStatus >= 2 ? (
                                                                    <Link
                                                                        classes={{ anchor: classes.anchorIcon }}
                                                                        className={classes.firstIcon}
                                                                        id={`btnEnableEdit_${permissionRequest.id}`}
                                                                        onClick={onClickEnableEdit}
                                                                    >
                                                                        <Tooltip
                                                                            id={`tltEnableEdit_${permissionRequest.id}`}
                                                                            title={resources.btnEdit}
                                                                            aria-label={resources.btnEdit}
                                                                        >
                                                                            <Icon
                                                                                name="edit"
                                                                                type="neutral"
                                                                            />
                                                                        </Tooltip>
                                                                    </Link>
                                                                ) : undefined}
                                                            </>
                                                        )}
                                                    />
                                                )}
                                        </Grid>
                                    </Grid>
                                ) : undefined}
                                {permissionRequest.facultyInfo ? (
                                    <Grid container justifyContent={isFaculty && permissionRequest.isMyInfo ? 'flex-end' : 'flex-start'}>
                                        <Grid item xs={isFaculty && permissionRequest.isMyInfo ? false : true}>
                                            <Chip
                                                classes={{ root: classes.chipStatusComments }}
                                                id="chpStatusPermission"
                                                label={(
                                                    <StatusLabel
                                                        id={`lblStatus_${permissionRequest.id}_${id}`}
                                                        text={getStatusText(permissionRequest.statusPermisionRequest)}
                                                        type={getStatusType(permissionRequest.statusPermisionRequest)}
                                                    />
                                                )}
                                            />
                                            <br />
                                            <div className={classes.centered}>
                                                <div>
                                                    <Avatar
                                                        backgroundNumber={permissionRequest.facultyInfo.colorFirstLetter}
                                                    >
                                                        {permissionRequest.facultyInfo.firstLetter}
                                                    </Avatar>
                                                </div>
                                                <div className={classes.commentsForStudent}>
                                                    <Grid container>
                                                        <Grid item xs>
                                                            <Text
                                                                className={classes.textOverChip}
                                                                color="textSecondary"
                                                                size="small"
                                                                verticalAlign="middle"
                                                            >
                                                                {permissionRequest.permissionDate ?
                                                                    `${permissionRequest.facultyInfo.fullName} (${permissionRequest.permissionDate})`
                                                                    : `${permissionRequest.facultyInfo.fullName}`}
                                                            </Text>
                                                        </Grid>
                                                    </Grid>
                                                    {isFaculty && permissionRequest.isMyInfo ? (
                                                        permissionRequest.editable ? (
                                                            <Grid container>
                                                                <Grid item xs>
                                                                    <Chip
                                                                        classes={{ root: classes.chipEditableComments }}
                                                                        id="chpPermissionEditableComments"
                                                                        label={(
                                                                            <>
                                                                                <TextField
                                                                                    classes={{ root: classes.commentsInChipEditable }}
                                                                                    error={permissionRequest.error}
                                                                                    helperText={permissionRequest.error ?
                                                                                        resources.lblCommentRequired : ''}
                                                                                    id={`txtComments_${permissionRequest.id}`}
                                                                                    label={resources.lblComments}
                                                                                    multiline
                                                                                    minRows={2}
                                                                                    type="text"
                                                                                    value={permissionRequest.permissionComments || ''}
                                                                                    onChange={onChangeCommentsForEditWithId}
                                                                                />
                                                                                <Link
                                                                                    classes={{ anchor: classes.anchorIcon }}
                                                                                    className={classes.firstIcon}
                                                                                    id={`btnEdit_${permissionRequest.id}`}
                                                                                    onClick={onClickEdit}
                                                                                >
                                                                                    <Tooltip
                                                                                        id={`tltEdit_${permissionRequest.id}`}
                                                                                        title={resources.btnAccept}
                                                                                        aria-label={resources.btnAccept}
                                                                                    >
                                                                                        <Icon
                                                                                            large
                                                                                            name="check-feedback"
                                                                                            type="success"
                                                                                        />
                                                                                    </Tooltip>
                                                                                </Link>
                                                                                <Link
                                                                                    classes={{ anchor: classes.anchorIcon }}
                                                                                    className={classes.secondIcon}
                                                                                    id={`btnCancelEdit_${permissionRequest.id}`}
                                                                                    onClick={onClickCancelEdit}
                                                                                >
                                                                                    <Tooltip
                                                                                        id={`tltCancelEdit_${permissionRequest.id}`}
                                                                                        title={resources.btnCancel}
                                                                                        aria-label={resources.btnCancel}
                                                                                    >
                                                                                        <Icon
                                                                                            large
                                                                                            name="close"
                                                                                            type="error"
                                                                                        />
                                                                                    </Tooltip>
                                                                                </Link>
                                                                            </>
                                                                        )}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        ) : (
                                                                <Chip
                                                                    classes={{ root: classes.chipCommonComments }}
                                                                    id="chpPermissionComments"
                                                                    label={(
                                                                        <>
                                                                            <Text
                                                                                className={classes.commentsInChip}
                                                                                color="inherit"
                                                                                component={permissionRequest.permissionComments ?
                                                                                    undefined : 'em'}
                                                                                size="small"
                                                                            >
                                                                                {permissionRequest.permissionComments || resources.lblTypeComments}
                                                                            </Text>
                                                                            {!Boolean(permissionRequest.overrideId
                                                                                || (permissionRequest.overrideInfo
                                                                                    && permissionRequest.overrideInfo.fullName)) ? (
                                                                                    <Link
                                                                                        classes={{ anchor: classes.anchorIcon }}
                                                                                        className={classes.firstIcon}
                                                                                        id={`btnEnableEdit_${permissionRequest.id}`}
                                                                                        onClick={onClickEnableEdit}
                                                                                    >
                                                                                        <Tooltip
                                                                                            id={`tltEnableEdit_${permissionRequest.id}`}
                                                                                            title={resources.btnEdit}
                                                                                            aria-label={resources.btnEdit}
                                                                                        >
                                                                                            <Icon
                                                                                                name="edit"
                                                                                                type="info"
                                                                                            />
                                                                                        </Tooltip>
                                                                                    </Link>
                                                                                ) : undefined}
                                                                        </>
                                                                    )}
                                                                />
                                                            )
                                                    ) : (
                                                            <>
                                                                <Chip
                                                                    classes={{ root: classes.chipCommonComments }}
                                                                    id="chpPermissionCommentsStudent"
                                                                    label={(
                                                                        <Text
                                                                            className={classes.commentsInChip}
                                                                            color="inherit"
                                                                            size="small"
                                                                        >
                                                                            {permissionRequest.permissionComments || resources.lblNoComments}
                                                                        </Text>
                                                                    )}
                                                                />
                                                                <br />
                                                            </>
                                                        )}
                                                </div>
                                            </div>
                                            {permissionRequest.overrideId
                                                || (permissionRequest.overrideInfo
                                                    && permissionRequest.overrideInfo.fullName) ? (
                                                    <div className={classes.overrideCommentsContainer}>
                                                        <Grid container>
                                                            <Grid item xs>
                                                                <Text
                                                                    className={classes.textOverChip}
                                                                    color="textSecondary"
                                                                    size="small"
                                                                    verticalAlign="middle"
                                                                >
                                                                    {`${(permissionRequest.overrideInfo
                                                                        && permissionRequest.overrideInfo.fullName) ?
                                                                        permissionRequest.overrideInfo.fullName
                                                                        : permissionRequest.overrideId} (${permissionRequest.overrideDate})`}
                                                                </Text>
                                                            </Grid>
                                                        </Grid>
                                                        <Chip
                                                            classes={{ root: classes.chipCommonComments }}
                                                            id="chpOverrideComments"
                                                            label={(
                                                                <Text
                                                                    className={classes.commentsInChip}
                                                                    color="inherit"
                                                                    size="small"
                                                                >
                                                                    {permissionRequest.overrideComments || resources.lblNoComments}
                                                                </Text>
                                                            )}
                                                        />
                                                        <br />
                                                    </div>
                                                ) : undefined}
                                        </Grid>
                                    </Grid>
                                ) : undefined}
                                {ipr < permissionRequestInfo.length - 1 ? (<Divider />) : undefined}
                            </Grid>
                        </Grid>
                    );
                })
                }
            </div>
        </>
    );
};
// #endregion Component

// Export: Component
export default withStyles(styles)(PermissionRequestDetail);